/**
 * journeyLoaders.ts — what the journey's waiting moments SAY, and when a
 * narrated checklist ("We’re checking your answers", "Setting up your health
 * check") is allowed to tick.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (CTO, 2026-09-24; the capture checklist 2026-09-25).
 *
 * Three waits, one module, because all three are rules about HONEST waiting
 * copy and all three render on public (pre-eligibility, or verdict-not-yet-
 * known) surfaces. The two checklists share ONE pacing engine
 * ({@link runPacedBeat}, section 4) and ONE renderer (`NarratedChecklist.tsx`);
 * only their steps, their clock and which step waits for the server differ.
 *
 * ── 1. THE ENTRY LOADER'S MESSAGE ───────────────────────────────────────────
 * JourneyPage shows ONE `BrandedLoader` while any of five reads is in flight.
 * It used to say "Connecting you to GP Service — Clinics" for all of them. The
 * CTO asked for a line that says what is actually happening, so
 * {@link journeyLoaderMessage} picks it from the flags the page already holds.
 * The most SPECIFIC lane wins over the generic clinic-config read, because the
 * flags overlap: a `?assessment=` restore also has the clinic config loading,
 * and the canonical widget hand-off (`?assessment=` + `#lead=`) also has the
 * restore running.
 *
 * ⚠️ PUBLIC-SAFE, ALWAYS. Every one of these shows before any eligibility
 * decision — the `#resume=` line even shows before the link itself has been
 * checked, so a forwarded or expired link can land on it. Category-level only
 * (R28), nothing about the visitor's body or treatment (the Art 9
 * `HEALTH_SHAPED` list), no price, no promise of a plan.
 * `journeyLoaders.spec.ts` sweeps every line against both lists.
 *
 * THE CONTENT CARD (2026-09-25; it replaced the one-line reassurance "notes"
 * the same day, was redesigned that evening from a carousel into a journey
 * that builds up, and at ~18:00 into an icon-led card that grows as it does).
 * On a fresh start only (the clinic read and the widget hand-off —
 * {@link journeyLoaderCard}) the journey look of the loader shows a small card
 * under its line: `ENTRY_LOADER_CARD` (`entryLoaderContent.ts`) — the journey
 * as four icon-led steps ("2-minute check", "No charge until approved", "UK
 * clinician review", "Approved? Sent to your door", each with one short line),
 * then three chips (a free call back, Switch & Save, our team), revealed one
 * after another and then static — every line a claim another screen already
 * makes, in its own words or a short form kept beside them. They are standing
 * claims about the service, not a claim about this wait — which is why they,
 * alone among these lines, may name the clinician review, a plan or the
 * saving — and they are never shown on a lane that can land after a verdict or
 * on a payment step.
 *
 * ── 2. THE BUILDING BEAT ────────────────────────────────────────────────────
 * The screen between the last answer and the verdict covers exactly ONE real
 * request: `POST …/assessments/:id/submit`, in which clinic-service's automated
 * rules engine computes the verdict and the response carries it back. The
 * checklist may only claim what that request does, in the order it does it:
 *
 *   1. "Saving your answers" → "Saved" — every answer was already POSTed and
 *      acknowledged question by question, and the submit that seals them is in
 *      flight from the first frame. Ticks after its readable dwell.
 *   2. "Checking your eligibility" → "Checked" — ticks ONLY WHEN THE SUBMIT
 *      RESPONSE HAS ARRIVED (the rules engine ran inside that request). Never
 *      on a timer alone: a slow server simply holds this step, however long it
 *      takes, and after {@link BuildingBeatTiming.stillWorkingAfter} the screen
 *      says so honestly ("Still working …") until the answer lands.
 *   3. "Preparing your results" → "Ready" — the portal choosing and drawing the
 *      next screen (and, on a hand-off that reached the end without usable
 *      measurements, the best-effort measurement read that runs before it —
 *      the `prepare` argument). Ticks only once its dwell has passed AND that
 *      read has settled, and then an "All done" beat shows the finished list
 *      for a moment before the next screen replaces it. (Review, 2026-09-25:
 *      the first cut said "All done" and then sat still while the read ran.)
 *
 * WHAT IT MUST NEVER SAY: that a clinician is looking at the answers (nobody
 * is — the verdict is automated), or anything that assumes the outcome. The
 * `not_eligible` group sees this screen too (CLIN-02), so no "your plan", no
 * "the right treatment for you". The now-lines and the past-tense words are held
 * to the same sweep as the labels (`journeyLoaders.spec.ts`, `r28.spec.tsx`).
 *
 * TIMING — A READABLE FLOOR (CTO, 2026-09-24: "the loaders go really fast … at
 * least have … a minimal time … allowing them to read what it is doing"). The
 * old 600/600/500ms beat ticked faster than a patient can read a line, so it
 * read as flicker. Each narrated step is now CURRENT for at least 1.2s —
 * saving 1300 · checking 1500 (a floor; it also waits for the answer) ·
 * preparing 1200 — then the "All done" beat holds 700ms: a 4.7s floor.
 * `journeyLoaders.spec.ts` pins "every step >= 1.2s, total >= 4s", so a later
 * "speed it up" edit fails loudly instead of quietly bringing the flicker back.
 * The floor applies to THIS narrated checklist only — a plain button spinner, an
 * error surface or a payment confirmation is never held behind it.
 *
 * ⚠️ SPECS. Journey specs that walk to the verdict with real timers would crawl
 * (or time out) at 4.7s a walk, so they pass a short timing FROM THE SPEC
 * (`testing/fastJourneyBeats.ts`, a `vi.mock` of this module that passes a
 * `timing` argument). Production code never branches on a test mode: the only
 * seam is the `timing` parameter below, which defaults to the real floor.
 *
 * FAILURE IS UNCHANGED. A rejected submit rejects this beat IMMEDIATELY — never
 * after the dwell — so JourneyPage's two-argument `then` still binds the
 * retryable "could not submit your answers" error to the submit and nothing
 * else, and it clears its own timers so a stale tick from a failed attempt can
 * never advance the checklist of the retry. A `prepare` that fails is NOT a
 * failure: it is an enrichment of a result the patient has already earned, so
 * its rejection just lets step 3 tick.
 *
 * ⚠️ WHAT DOES NOT WAIT FOR THE FLOOR. The floor paces the SCREEN, and only
 * the screen. The funnel's `eligibility_result` push and the "Keeping in
 * touch" marketing grant hang off the submit's OWN answer in JourneyPage, not
 * off this beat — a patient who closes the tab during the "All done" beat has
 * still completed the assessment, and their tick still counts. What DOES wait
 * is the verdict's screen, whichever it is: the not-eligible Stop screen shares
 * the same floor as "Good news" (CLIN-02 — the checklist is neutral for both
 * outcomes, and a stop that arrived 3s sooner than a pass would announce the
 * verdict through the timing before the screen says it).
 *
 * ── 3. THE CAPTURE BEAT (2026-09-25, CTO) ───────────────────────────────────
 * "When you click 'Check my eligibility,' that button hangs there … there is an
 * interesting way to say … we are checking your responses … and then take the
 * user to the chart." On stage the lead POST can wait on a cold service for
 * seconds (a minute in the worst case), and the only sign of life was
 * "Checking…" on a button. Step 0 now replaces its form, in place, with a
 * narrated checklist over the ONE request it covers — `POST /v1/clinic/leads`
 * — and every line was checked against what the code actually does:
 *
 *   1. "Saving your details" ("The details you just entered") → "Saved" ONLY
 *      WHEN THE LEAD POST HAS ANSWERED. A slow server holds this step, however
 *      long. If the POST FAILED, the step does not tick: it says "Not saved
 *      this time — we’ll carry on without it" (the patient still carries on
 *      without a lead id, exactly as before — never blocked), because a green
 *      tick over a save that did not happen is a claim this module does not
 *      make. The status region says it too, ahead of the next step (see
 *      `NarratedChecklist.tsx`, "MISSED"). (Review, 2026-09-25: the first cut
 *      said "you can carry on", which reads as if the patient has something to
 *      do — nothing is asked of them, the list moves on by itself. The now-line
 *      was added the same day: this is the step that can be on screen longest,
 *      and a blank line under it left an empty band in its white row.)
 *   2. "Calculating your projection" ("From the weight you gave us") →
 *      "Calculated" — the next screen's figure and curve are worked out from
 *      the WEIGHT alone (`projection.ts`: round(weight × 0.21) over 10 months).
 *      ⚠️ It does NOT say "Working out your BMI": the next screen shows no BMI
 *      (`bmiOf` is drawn only on the Result screen, after the questionnaire),
 *      and the height is not used until then either — so neither is claimed.
 *   3. "Drawing your chart" ("Showing what could be within reach" — the next
 *      screen's own heading) → "Ready", then a short "All done" beat.
 *
 * TIMING — 1000 · 1000 · 900 + a 700ms done beat = a 3.6s floor, step 1 ALSO
 * gated on the server. Shorter than the building beat's on purpose: these
 * lines are shorter, and this stands between a patient and the first thing
 * the journey gives back. The closing beat is the building beat's own 700ms
 * (review, 2026-09-25: at 400ms "All done — opening your chart." flashed and
 * went, under the readable floor, and the stage change that follows moves
 * focus — so a screen reader was likely cut off part-way through it; the two
 * loaders now also close the same way). "Still working — this can take a few
 * more seconds." after 4s, and after 12s the calmer "This is running a little
 * longer than usual." (only while the POST is unanswered; both go the moment
 * it lands). It stops there: the intro above it already says "Please keep
 * this page open", and the first cut said it twice on one screen. ⚠️ Not
 * "Taking a little longer": `taking` is on the Art 9 `HEALTH_SHAPED` list —
 * the same reason "Taking you back…" became "Returning to your answers".
 * `journeyLoaders.spec.ts` pins the floor (each step >= 0.9s, 3–4s in all),
 * and specs that walk Step 0 use the same spec-side clock as the building beat
 * (`testing/fastJourneyBeats.ts`).
 *
 * ⚠️ THE MINUTE IS NARRATED, NOT SHORTENED — A DECISION (review, 2026-09-25).
 * Step 1 has NO upper limit, and the lead POST (`api/client.ts` `request()`)
 * has no client-side timeout: on the cold stage POST the CTO measured (~56s),
 * the patient sees "Still working" at 4s and, from 12s until the answer, the
 * calmer line. That is deliberate, and it is not the fix for the minute:
 *   - capping the step (say at 15s) and carrying on would draw "Not saved"
 *     over a save that has usually ALREADY happened — clinic-service commits
 *     the lead row and only THEN awaits its `lead_captured` emit to reporting
 *     (`leads.service.ts` `create()`), and that emit is where this branch's
 *     diagnosis puts the minute (CHANGELOG) — so the line would be false,
 *     and the journey would lose the lead id that the assessment create, the
 *     readiness answer and the `eligibility_result` push all key on, unless a
 *     late-attach lane were built through JourneyPage (a funnel change, not a
 *     loader change);
 *   - the minute itself is backend (the awaited emit into a cold reporting
 *     service — an infra setting such as minimum instances, or a
 *     fire-and-forget emit in clinic-service), which this loader can only
 *     narrate.
 * A connection that never answers is bounded only by the browser's and the
 * server's own timeouts; a client-side timeout would belong in `api/client.ts`
 * for every request, not in this module.
 *
 * ⚠️ WHAT DOES NOT WAIT FOR THE FLOOR. Funnel C1 (`emitGenerateLeadOnce`) is
 * pushed where it always was — the moment `createLead` returns, inside
 * CaptureStep — not after the checklist. The floor paces the SCREEN only.
 */

import type { LoaderJourney } from '../ui/LoaderJourneyCard';
import { ENTRY_LOADER_CARD } from './entryLoaderContent';

/* ── 1. The entry loader ─────────────────────────────────────────────────── */

/** Every line the journey's entry loader can show. Exported for the copy sweep. */
export const JOURNEY_LOADER_COPY = {
  /** The clinic config read — every journey entry. */
  opening: 'Opening your health check',
  /** The Webflow widget hand-off (`#lead=` / legacy `?lead=`): details were entered on the widget. */
  handOff: 'Picking up where you left off',
  /** `?assessment=` restore, and the result-summary read after the emailed-code gate. */
  restoring: 'Restoring your progress',
  /** The in-flow hard-stop "Back": a new assessment is minted and the earlier answers replayed. */
  backToAnswers: 'Returning to your answers',
  /** `#resume=` plan link: the grant mint. Shown BEFORE the link is known to be good. */
  checkingLink: 'Checking your link',
  /**
   * Back from the bank's 3D Secure page (`#checkout-return=`, 2026-09-25): the
   * card step is being re-entered to finish an order the patient already
   * confirmed. The SAME words CheckoutFlow's loader shows while it finishes the
   * order, so the wait reads as one wait. Post-verdict by construction — the
   * patient was on the card step.
   */
  finishingOrder: 'Finishing your order',
} as const;

/** The JourneyPage flags that hold the entry loader up (the clinic read is the fallback). */
export interface JourneyLoaderFlags {
  hardStopResuming: boolean;
  resumeGrantPending: boolean;
  leadPending: boolean;
  restoring: boolean;
  /** The page was opened by a 3D Secure return (see `checkout/setupRedirectReturn.ts`). */
  paymentReturn?: boolean;
}

/** The entry loader's line — the most specific lane in flight wins (see the header). */
export function journeyLoaderMessage(flags: JourneyLoaderFlags): string {
  // Before every other lane: a 3D Secure return is the one entry that is
  // finishing a payment, and the only other flag it raises is the grant
  // (`resumeGrantPending`), whose "Checking your link" would be untrue here.
  if (flags.paymentReturn) return JOURNEY_LOADER_COPY.finishingOrder;
  if (flags.hardStopResuming) return JOURNEY_LOADER_COPY.backToAnswers;
  if (flags.resumeGrantPending) return JOURNEY_LOADER_COPY.checkingLink;
  if (flags.leadPending) return JOURNEY_LOADER_COPY.handOff;
  if (flags.restoring) return JOURNEY_LOADER_COPY.restoring;
  return JOURNEY_LOADER_COPY.opening;
}

/**
 * Which lanes get the content card (the journey look of `BrandedLoader`,
 * 2026-09-25): a FRESH START only — the clinic read (a direct `/weightloss`
 * entry) and the widget hand-off, both before a single answer exists, on the
 * self-serve lane that always takes the hold route. Never a restore, a
 * hard-stop return or a plan link: those can land after a verdict, or straight
 * on a payment step (a plan link can be the clinician-led lane, which takes
 * payment at once), where "no charge until…" and "how it works" are not this
 * screen's to say. One rule for the WHOLE card (its steps and its chips), so
 * no part of it can be shown on a different lane. Returns
 * the module constant (or undefined), so a re-render never hands the card a
 * new object.
 */
export function journeyLoaderCard(flags: JourneyLoaderFlags): LoaderJourney | undefined {
  const message = journeyLoaderMessage(flags);
  return message === JOURNEY_LOADER_COPY.opening || message === JOURNEY_LOADER_COPY.handOff ? ENTRY_LOADER_CARD : undefined;
}

/* ── 2. The building beat ────────────────────────────────────────────────── */

/**
 * Which checklist step is CURRENT. Earlier steps are done; later ones are to
 * come. `done` is the closing beat: every step ticked, the list no longer busy.
 */
export type BuildingPhase = 'saving' | 'checking' | 'preparing' | 'done';

/** A step's phase — every phase but the closing beat. */
export type BuildingStepPhase = Exclude<BuildingPhase, 'done'>;

export interface BuildingStep {
  phase: BuildingStepPhase;
  /** What we are doing. The status region announces this once, as the step starts. */
  label: string;
  /** The line under the label while the step is CURRENT (null: the label says it all). */
  now: string | null;
  /** The past-tense word that replaces the now-line once the step has ticked. */
  done: string;
}

/** The checklist, in order. Each label maps to real work — see the header before editing. */
export const BUILDING_STEPS: ReadonlyArray<BuildingStep> = [
  { phase: 'saving', label: 'Saving your answers', now: null, done: 'Saved' },
  { phase: 'checking', label: 'Checking your eligibility', now: 'Comparing with our clinical criteria', done: 'Checked' },
  { phase: 'preparing', label: 'Preparing your results', now: 'Getting your next step ready', done: 'Ready' },
];

/**
 * The checklist's other lines. Public copy, neutral for both outcomes — swept
 * with the step lines (`journeyLoaders.spec.ts`, `r28.spec.tsx`).
 */
export const BUILDING_COPY = {
  /** Under the heading while the steps run. */
  intro: 'This usually takes a few seconds. Please keep this page open.',
  /** Only once the answer is later than `stillWorkingAfter`; gone the moment it lands. */
  stillWorking: 'Still working — this can take a few more seconds.',
  /** The closing beat — replaces the intro, and is the status region's last line. */
  allDone: 'All done — opening your results.',
} as const;

export interface BuildingBeatTiming {
  /** How long step 1 is current before it ticks. */
  saving: number;
  /** The FLOOR on step 2 — it also waits for the submit response, however long. */
  checking: number;
  /** How long step 3 is current before it ticks. */
  preparing: number;
  /** How long the finished list ("All done") shows before the next screen replaces it. */
  done: number;
  /** From the start: the answer is late enough to say "Still working" (only while it is still awaited). */
  stillWorkingAfter: number;
}

/**
 * The production clock: every narrated step >= 1.2s, a 4.7s floor in all. See
 * "TIMING" in the header before changing — `journeyLoaders.spec.ts` pins it.
 */
export const BUILDING_BEAT_MS: Readonly<BuildingBeatTiming> = {
  saving: 1300,
  checking: 1500,
  preparing: 1200,
  done: 700,
  stillWorkingAfter: 4000,
};

/** What the building screen draws at any moment. */
export interface BuildingBeatView {
  phase: BuildingPhase;
  /** True while the answer is later than `stillWorkingAfter`; false again the moment it lands. */
  stillWorking: boolean;
}

/** The first frame of every beat — a retry starts here again, never mid-list. */
export const BUILDING_BEAT_START: Readonly<BuildingBeatView> = { phase: 'saving', stillWorking: false };

/**
 * Run the submit under the staged checklist.
 *
 * Calls `onView` synchronously with {@link BUILDING_BEAT_START}; moves to
 * `'checking'` after the saving dwell; to `'preparing'` only once BOTH the
 * checking floor has passed AND `submit` has resolved; to `'done'` only once
 * BOTH the preparing dwell has passed AND `prepare` (if given) has settled;
 * then resolves with the submit's own value after the done beat. If the answer
 * is still awaited at `stillWorkingAfter`, the view gains `stillWorking: true`
 * until it lands. Rejects with the submit's own error the moment it rejects —
 * whatever step the list is on — and never calls `onView` again after settling
 * either way.
 *
 * `prepare` is the best-effort work "Preparing your results" covers. It starts
 * the moment the submit answers (alongside the rest of the floor, so it costs
 * no extra time when it is quick), receives the submit's value, and its
 * rejection is swallowed — it can hold step 3, never fail the beat. Production
 * passes `undefined` for `timing` to keep the real floor (the default — see
 * `testing/fastJourneyBeats.ts`, which relies on production never naming it).
 *
 * Since 2026-09-25 this is the building beat's PLAN handed to the shared engine
 * ({@link runPacedBeat}); every rule above is the engine's, and
 * `journeyLoaders.spec.ts` still pins each one through THIS function.
 */
export function runBuildingBeat<T>(
  submit: Promise<T>,
  onView: (view: BuildingBeatView) => void,
  timing: BuildingBeatTiming = BUILDING_BEAT_MS,
  prepare?: (value: T) => Promise<unknown>,
): Promise<T> {
  return runPacedBeat(
    submit,
    {
      steps: [
        { phase: 'saving', floor: timing.saving },
        // "Checking your eligibility" — the rules engine ran inside the submit.
        { phase: 'checking', floor: timing.checking, waitsFor: 'answer' },
        // "Preparing your results" — covers the best-effort `prepare` read.
        { phase: 'preparing', floor: timing.preparing, waitsFor: 'prepare' },
      ],
      done: timing.done,
      stillWorkingAfter: timing.stillWorkingAfter,
    },
    // The building view has no second tier, so it never carries one.
    ({ phase, stillWorking }) => onView({ phase, stillWorking }),
    prepare,
  );
}

/* ── 3. The capture beat ─────────────────────────────────────────────────── */

/** Which capture step is CURRENT; `done` is the closing beat. */
export type CapturePhase = 'saving' | 'calculating' | 'drawing' | 'done';

/** A capture step's phase — every phase but the closing beat. */
export type CaptureStepPhase = Exclude<CapturePhase, 'done'>;

export interface CaptureChecklistStep {
  phase: CaptureStepPhase;
  label: string;
  now: string | null;
  done: string;
  /**
   * What the step says INSTEAD of `done` when the work it names did not happen
   * (the lead POST failed). Only the save can fail; the other two are the
   * portal's own work on numbers it already holds.
   */
  missed?: string;
}

/** The capture checklist, in order. Each line is checked against the code — see section 3 of the header. */
export const CAPTURE_STEPS: ReadonlyArray<CaptureChecklistStep> = [
  { phase: 'saving', label: 'Saving your details', now: 'The details you just entered', done: 'Saved', missed: 'Not saved this time — we’ll carry on without it' },
  { phase: 'calculating', label: 'Calculating your projection', now: 'From the weight you gave us', done: 'Calculated' },
  { phase: 'drawing', label: 'Drawing your chart', now: 'Showing what could be within reach', done: 'Ready' },
];

/** The capture checklist's other lines — public copy, swept with the steps. */
export const CAPTURE_COPY = {
  /** The eyebrow when the first name typed on Step 0 does not read as one (`greetingName`). */
  thanks: 'Thank you',
  title: 'Setting up your health check',
  /** The same sentence as the building beat's: one promise, worded once. */
  intro: BUILDING_COPY.intro,
  /** Only while the lead POST is later than `stillWorkingAfter`. */
  stillWorking: BUILDING_COPY.stillWorking,
  /**
   * Only while it is later than `longerAfter` — calmer. It does NOT repeat
   * "please keep this page open": the intro above it already says so.
   */
  longerThanUsual: 'This is running a little longer than usual.',
  allDone: 'All done — opening your chart.',
} as const;

/** The eyebrow: "Thanks, Sam" with the first name typed on this screen, else "Thank you". */
export function captureThanks(firstName: string | null): string {
  return firstName ? `Thanks, ${firstName}` : CAPTURE_COPY.thanks;
}

export interface CaptureBeatTiming {
  /** The FLOOR on step 1 — it also waits for the lead POST, however long. */
  saving: number;
  calculating: number;
  drawing: number;
  /** The "All done" beat. */
  done: number;
  /** From the start: the POST is late enough to say "Still working". */
  stillWorkingAfter: number;
  /** From the start: late enough for the calmer "longer than usual" line. */
  longerAfter: number;
}

/**
 * The production clock: 1000 · 1000 · 900 + 700 = a 3.6s floor. See section 3
 * of the header before changing — `journeyLoaders.spec.ts` pins it.
 */
export const CAPTURE_BEAT_MS: Readonly<CaptureBeatTiming> = {
  saving: 1000,
  calculating: 1000,
  drawing: 900,
  done: 700,
  stillWorkingAfter: 4000,
  longerAfter: 12000,
};

/** What the capture loader draws at any moment. */
export interface CaptureBeatView {
  phase: CapturePhase;
  /** The POST is later than `stillWorkingAfter` (and still unanswered). */
  stillWorking: boolean;
  /** …and later than `longerAfter`. Implies `stillWorking`. */
  longerThanUsual: boolean;
  /** How the save went: `null` until the POST has answered, then true/false. */
  saved: boolean | null;
}

/** The first frame of every capture beat. */
export const CAPTURE_BEAT_START: Readonly<CaptureBeatView> = {
  phase: 'saving',
  stillWorking: false,
  longerThanUsual: false,
  saved: null,
};

/**
 * Run the lead save under the capture checklist.
 *
 * `saved` is the save's OUTCOME, never its error: it resolves `true` when the
 * lead POST answered with a lead and `false` when it failed — the caller keeps
 * today's failure semantics (carry on without a lead id) and only tells the
 * checklist which line is true. A rejection is read as `false` too, so this
 * beat NEVER rejects: nothing here can block the patient.
 *
 * Calls `onView` synchronously with {@link CAPTURE_BEAT_START}; step 1 ticks at
 * the LATER of its floor and the outcome; steps 2 and 3 run their dwells; then
 * the "All done" beat, and the promise resolves with the outcome. Production
 * passes no `timing` (the real floor — `testing/fastJourneyBeats.ts` relies on
 * that).
 */
export function runCaptureBeat(
  saved: Promise<boolean>,
  onView: (view: CaptureBeatView) => void,
  timing: CaptureBeatTiming = CAPTURE_BEAT_MS,
): Promise<boolean> {
  let outcome: boolean | null = null;
  // Set BEFORE the engine hears the answer: the engine's handler is attached to
  // `landed`, which settles only after this callback has returned.
  const landed = saved.then(
    (ok) => {
      outcome = ok === true;
      return outcome;
    },
    () => {
      outcome = false;
      return outcome;
    },
  );
  return runPacedBeat(
    landed,
    {
      steps: [
        { phase: 'saving', floor: timing.saving, waitsFor: 'answer' },
        { phase: 'calculating', floor: timing.calculating },
        { phase: 'drawing', floor: timing.drawing },
      ],
      done: timing.done,
      stillWorkingAfter: timing.stillWorkingAfter,
      longerAfter: timing.longerAfter,
    },
    (view) => onView({ ...view, saved: outcome }),
  );
}

/* ── 4. The pacing engine both checklists share ──────────────────────────── */

/** One narrated step's pacing. */
interface PacedStep<P extends string> {
  phase: P;
  /** How long the step is CURRENT at least — the readable floor. */
  floor: number;
  /**
   * What else it waits for before it may tick: `'answer'` — the request the
   * checklist covers has answered; `'prepare'` — the best-effort prepare work
   * has settled. Never a timer alone.
   */
  waitsFor?: 'answer' | 'prepare';
}

interface PacedPlan<P extends string> {
  steps: ReadonlyArray<PacedStep<P>>;
  /** The closing "All done" beat. */
  done: number;
  /** From the start, while the answer is still awaited. */
  stillWorkingAfter: number;
  /** An optional second, calmer tier (the capture beat's 12s line). */
  longerAfter?: number;
}

interface PacedView<P extends string> {
  phase: P | 'done';
  stillWorking: boolean;
  longerThanUsual: boolean;
}

/** Both "late" flags down — the answer has landed. */
const ON_TIME = { stillWorking: false, longerThanUsual: false } as const;

/**
 * THE ONE PACING ENGINE. Draws `plan.steps` in order: each step is current for
 * at least its `floor` and until whatever it `waitsFor` has happened, then the
 * next starts; after the last, a `'done'` view and, `plan.done` later, the
 * promise resolves with the answer's value. Never before the answer — the
 * beat resolves WITH it.
 *
 * Honest about a slow answer, and ONLY while it is actually awaited:
 * `stillWorking` from `stillWorkingAfter`, `longerThanUsual` (+ `stillWorking`)
 * from `longerAfter`, both dropped the moment it lands. `prepare(value)` starts
 * the moment the answer lands, and its failure (thrown or rejected) is
 * swallowed — it can hold its step, never fail the beat. A rejected answer
 * rejects the beat AT ONCE, whatever step it is on, and cancels every pending
 * tick, so a dead beat never draws again (a retry cannot inherit a stale step).
 */
function runPacedBeat<P extends string, T>(
  answerPromise: Promise<T>,
  plan: PacedPlan<P>,
  onView: (view: PacedView<P>) => void,
  prepare?: (value: T) => Promise<unknown>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let view: PacedView<P> = { phase: plan.steps[0].phase, ...ON_TIME };
    let index = 0;
    let floorPassed = false;
    let prepared = !prepare;
    let finished = false;
    let answer: { value: T } | null = null;

    const show = (next: Partial<PacedView<P>>) => {
      view = { ...view, ...next };
      onView(view);
    };
    const later = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, ms));
    };
    // THE guard against a stale tick: once settled, every pending step is
    // cancelled. (A settled promise's own handlers cannot fire again, so the
    // timers are the only thing that could.)
    const settle = () => {
      for (const timer of timers) clearTimeout(timer);
    };

    // The current step ticks at the LATER of its floor and what it waits for.
    // Every caller (its floor, the answer, the prepare work) reaches this; the
    // checks make an early or repeated call inert. Returns whether it moved.
    const advance = (): boolean => {
      if (finished || !floorPassed) return false;
      const step = plan.steps[index];
      if (step.waitsFor === 'answer' && !answer) return false;
      if (step.waitsFor === 'prepare' && !prepared) return false;
      if (index + 1 < plan.steps.length) {
        // Once the answer is in, the "late" flags are down (they only ever
        // rise while it is awaited), so every later view says so.
        show({ phase: plan.steps[index + 1].phase, ...(answer ? ON_TIME : {}) });
        begin(index + 1);
        return true;
      }
      if (!answer) return false;
      finished = true;
      const { value } = answer;
      show({ phase: 'done' });
      later(plan.done, () => {
        settle();
        resolve(value);
      });
      return true;
    };

    const begin = (next: number) => {
      index = next;
      floorPassed = false;
      later(plan.steps[next].floor, () => {
        floorPassed = true;
        advance();
      });
    };

    onView(view);
    begin(0);
    later(plan.stillWorkingAfter, () => {
      if (!answer) show({ stillWorking: true });
    });
    const { longerAfter } = plan;
    if (longerAfter !== undefined) {
      later(longerAfter, () => {
        if (!answer) show({ stillWorking: true, longerThanUsual: true });
      });
    }

    answerPromise.then(
      (value) => {
        answer = { value };
        if (prepare) {
          // Best-effort: a throw (sync or async) or a rejection only means the
          // enrichment is missing — its step still ticks once it has settled.
          Promise.resolve()
            .then(() => prepare(value))
            .catch(() => undefined)
            .then(() => {
              prepared = true;
              advance();
            });
        }
        // The answer landing ends "still working" at once, even when the
        // current step's floor (a spec's short timing) has not passed yet.
        if (!advance() && (view.stillWorking || view.longerThanUsual)) show(ON_TIME);
      },
      (err: unknown) => {
        settle();
        reject(err);
      },
    );
  });
}
